<template>
  <!-- Detalle de UN power block (al enfocar con doble click). Presentación pura: recibe
       el pictograma (ya cargado en el panel de info) + el alias de company. Replica la
       lógica del dashboard (ProgressDetail/SummaryCard) con un diseño pro acorde al visor:
       cards de resumen por capa + progreso por estados + incidencias por estados. -->
  <div class="epc-block-info">
    <!-- Cabecera del bloque: nombre + actividad + barra de avance de la actividad -->
    <div class="epc-bi-head">
      <span class="epc-bi-badge">PB</span>
      <div class="epc-bi-head-text">
        <div class="epc-bi-name">{{ block.name }}</div>
        <div v-if="activityLabel" class="epc-bi-activity">{{ activityLabel }}</div>
      </div>
    </div>
    <div class="epc-bi-progress">
      <div class="epc-bi-bar"><div class="epc-bi-bar-fill" :style="{ width: activityProgress + '%' }"></div></div>
      <span class="epc-bi-pct">{{ activityProgress }}%</span>
    </div>

    <!-- Resumen por capa: tabla comparativa (Capa · Total · Ejecutado · % [+ Incidencias
         si no x-elio]). La fila de la capa actual (vista activa) va resaltada. -->
    <div class="epc-bi-section">
      <h5 class="epc-bi-title">{{ $t('pv_dashboard_summary') }}</h5>
      <table class="epc-bi-table">
        <thead>
          <tr>
            <th class="epc-bi-th-left">{{ $t('component') }}</th>
            <th>{{ $t('total') }}</th>
            <th>{{ $t('executed2') }}</th>
            <th v-if="companyAlias !== 'x-elio'">{{ $t('issues') }}</th>
            <th>%</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="lyr in layers" :key="'row-' + lyr.code"
            :class="{ 'epc-bi-row--active': isActiveLayer(lyr) }">
            <td class="epc-bi-td-left">
              <span v-if="isActiveLayer(lyr)" class="epc-bi-here">●</span>{{ layerLabel(lyr) }}
            </td>
            <td>{{ fmt(getTotal(lyr)) }}</td>
            <td>{{ fmt(getExecuted(lyr)) }}</td>
            <td v-if="companyAlias !== 'x-elio'">{{ fmt(getIncidents(lyr)) }}</td>
            <td class="epc-bi-td-pct">{{ layerPct(lyr) }}%</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Progreso por estados (por capa: color + nombre + conteo) -->
    <div class="epc-bi-section">
      <h5 class="epc-bi-title">{{ $t('progress_by_status') }}</h5>
      <div v-for="lyr in layers" :key="'prog-' + lyr.code" class="epc-bi-states"
        :class="{ 'epc-bi-states--active': isActiveLayer(lyr) }">
        <p class="epc-bi-states-layer">
          <span v-if="isActiveLayer(lyr)" class="epc-bi-here">●</span>{{ layerLabel(lyr) }}
        </p>
        <div v-if="!(lyr.statuses && lyr.statuses.length)" class="epc-bi-empty">—</div>
        <div v-for="state in lyr.statuses" :key="'ps-' + lyr.code + '-' + state.id" class="epc-bi-state-row">
          <span class="epc-bi-dot" :style="{ backgroundColor: state.color }"></span>
          <span class="epc-bi-state-name">{{ state.name }}</span>
          <span class="epc-bi-state-count">{{ fmt(getStatusProgress(lyr.code, state.id)) }}</span>
        </div>
      </div>
    </div>

    <!-- Incidencias por estados -->
    <div class="epc-bi-section">
      <h5 class="epc-bi-title">{{ $t('pv_dashboard_incidents_by_status') }}</h5>
      <div v-for="lyr in layers" :key="'inc-' + lyr.code" class="epc-bi-states"
        :class="{ 'epc-bi-states--active': isActiveLayer(lyr) }">
        <p class="epc-bi-states-layer">
          <span v-if="isActiveLayer(lyr)" class="epc-bi-here">●</span>{{ layerLabel(lyr) }}
        </p>
        <div v-if="!(lyr.incident_statuses && lyr.incident_statuses.length)" class="epc-bi-empty">—</div>
        <div v-for="state in lyr.incident_statuses" :key="'is-' + lyr.code + '-' + state.id" class="epc-bi-state-row">
          <span class="epc-bi-dot" :style="{ backgroundColor: state.color }"></span>
          <span class="epc-bi-state-name">{{ state.name }}</span>
          <span class="epc-bi-state-count">{{ fmt(getStatusProgress(lyr.code, state.id, 'incident')) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ViewerBlockInfoContent",

  props: {
    block: { type: Object, required: true },        // pictograma enfocado (del browse)
    companyAlias: { type: String, default: '' },
    activeLayer: { type: String, default: null },    // capa de la vista actual → se resalta
  },

  computed: {
    layers() {
      return this.block.layers || []
    },
    activityLabel() {
      const code = this.block.activity_planning_code
      const name = this.block.activity_associated
      if (code && name) return code + ' · ' + name
      return name || code || null
    },
    activityProgress() {
      return this._truncate(parseFloat(this.block.activity_progress) || 0, 2)
    },
  },

  methods: {
    // Nombre de la capa en el idioma del usuario (clave pilings/structures/modules; si no
    // hay traducción, el nombre de BD).
    layerLabel(lyr) {
      return this.$te(lyr.code) ? this.$t(lyr.code) : lyr.name
    },

    // La capa actual (la vista en la que está el usuario) → se resalta en el detalle.
    isActiveLayer(lyr) {
      return this.activeLayer != null && lyr.code === this.activeLayer
    },

    getTotal(lyr) {
      return parseFloat((this.block.metadata || {})['total_' + lyr.code]) || 0
    },

    getExecuted(lyr) {
      if (this.companyAlias === 'solaring') {
        const layer = this.layers.find(l => l.code === lyr.code)
        const config = layer && layer.metadata ? layer.metadata.config : null
        const avanceMethod = config && config.statuses ? config.statuses.avance_method : null
        const progressStatuses = layer && layer.metadata ? layer.metadata.progress_statuses : null
        if (!progressStatuses) return 0
        const nameToFind = 'Ejecutado (subcontratista)'
        if (avanceMethod === 'grouped') {
          for (const phase of Object.values(progressStatuses)) {
            const status = Object.values(phase.statuses || {}).find(s => s.name === nameToFind)
            if (status) return status.advance ?? 0
          }
          return 0
        }
        const result = Object.values(progressStatuses).find(s => s.name === nameToFind)
        return result ? (result.advance ?? 0) : 0
      }
      const tp = (this.block.metadata || {}).total_progress
      return parseFloat(tp ? (tp[lyr.code] ?? 0) : 0) || 0
    },

    getIncidents(lyr) {
      const layer = this.layers.find(l => l.code === lyr.code)
      if (layer && layer.metadata && layer.metadata.incident_statuses) {
        let total = 0
        for (const key in layer.metadata.incident_statuses) {
          if (Object.prototype.hasOwnProperty.call(layer.metadata.incident_statuses, key)) {
            total += parseFloat(layer.metadata.incident_statuses[key].advance ?? 0) || 0
          }
        }
        return total
      }
      return 0
    },

    getProgress(lyr) {
      const metadata = lyr.metadata
      if (metadata && metadata.progress_layer != null) {
        return parseFloat(metadata.progress_layer.advance_normalize ?? 0) || 0
      }
      return 0
    },

    // Porcentaje mostrado en la card: x-elio usa ejecutado/total; el resto, el avance
    // normalizado de la capa (idéntico a SummaryCard del dashboard).
    layerPct(lyr) {
      if (this.companyAlias === 'x-elio') {
        const total = this.getTotal(lyr)
        const exec = this.getExecuted(lyr)
        return exec > 0 && total > 0 ? this._truncate((exec / total) * 100, 2) : 0
      }
      return this._truncate(this.getProgress(lyr), 2)
    },

    // Conteo por estado (progreso/incidencia) — replica dashboard/ProgressDetail.
    getStatusProgress(type, idState, typeStatus = 'progress') {
      if (this.companyAlias !== 'ignis' && this.companyAlias !== 'solaring') {
        const sp = (this.block.metadata || {}).statuses_progress
        if (sp != null && sp[type]) return sp[type][idState] ?? 0
        return 0
      }

      const lyr = this.layers.find(layer => layer.code === type)

      if (typeStatus === 'progress') {
        if (lyr && lyr.metadata && lyr.metadata.progress_statuses) {
          const cfg = lyr.metadata.config.statuses
          const statusProgress = lyr.metadata.progress_statuses
          if (cfg.avance_method === 'grouped') {
            for (const key in statusProgress) {
              if (statusProgress[key].statuses[idState]) {
                return statusProgress[key].statuses[idState].advance ?? 0
              }
            }
            return 0
          }
          return statusProgress[idState] ? (statusProgress[idState].advance ?? 0) : 0
        }
      } else if (typeStatus === 'incident') {
        if (lyr && lyr.metadata && lyr.metadata.incident_statuses) {
          const incidentStatuses = lyr.metadata.incident_statuses
          return incidentStatuses[idState] ? (incidentStatuses[idState].advance ?? 0) : 0
        }
      }

      return 0
    },

    // Formato compacto: entero si no hay decimales, si no 2 decimales (evita "12.000000").
    fmt(num) {
      const n = Number(num) || 0
      return Number.isInteger(n) ? n : this._truncate(n, 2)
    },

    _truncate(num, decimals) {
      const factor = Math.pow(10, decimals)
      return Math.floor(num * factor) / factor
    },
  },
}
</script>

<style scoped>
.epc-block-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Cabecera del bloque */
.epc-bi-head {
  display: flex;
  align-items: center;
  gap: 9px;
}

.epc-bi-badge {
  flex: none;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: #3a6bc8;
  background: #e8eef8;
  padding: 3px 7px;
  border-radius: 6px;
  line-height: 1;
}

.epc-bi-head-text {
  min-width: 0;
}

.epc-bi-name {
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.epc-bi-activity {
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.epc-bi-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}

.epc-bi-bar {
  flex: 1;
  height: 7px;
  background: #eef0f4;
  border-radius: 999px;
  overflow: hidden;
}

.epc-bi-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #3a6bc8, #5a84d8);
  border-radius: 999px;
  transition: width 0.2s ease;
}

.epc-bi-pct {
  font-size: 13px;
  font-weight: 800;
  color: #3a6bc8;
  min-width: 42px;
  text-align: right;
}

/* Secciones */
.epc-bi-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.epc-bi-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a93a3;
  margin: 0;
}

/* Tabla comparativa de resumen por capa */
.epc-bi-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e6e8ef;
  border-radius: 10px;
  overflow: hidden;
}

.epc-bi-table thead th {
  background: #f4f6fb;
  color: #6b7280;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 6px 8px;
  text-align: right;
  border-bottom: 1px solid #e6e8ef;
  white-space: nowrap;
}

.epc-bi-table .epc-bi-th-left {
  text-align: left;
}

.epc-bi-table tbody td {
  font-size: 12px;
  color: #1f2937;
  padding: 6px 8px;
  text-align: right;
  border-bottom: 1px solid #f0f1f6;
  white-space: nowrap;
}

.epc-bi-table tbody tr:last-child td {
  border-bottom: none;
}

.epc-bi-td-left {
  text-align: left !important;
  font-weight: 600;
  text-transform: capitalize;
}

.epc-bi-td-pct {
  font-weight: 800;
  color: #3a6bc8;
}

/* Fila/sección de la capa actual: resaltado azul suave con marca a la izquierda. */
.epc-bi-row--active td {
  background: #e8eef8;
}

.epc-bi-row--active .epc-bi-td-left {
  box-shadow: inset 3px 0 0 #3a6bc8;
}

.epc-bi-here {
  color: #3a6bc8;
  font-size: 9px;
  margin-right: 5px;
  vertical-align: middle;
}

.epc-bi-states--active {
  background: #e8eef8;
  border-color: #cdddf4;
  box-shadow: inset 3px 0 0 #3a6bc8;
}

/* Listas de estados */
.epc-bi-states {
  border: 1px solid #eceef3;
  border-radius: 9px;
  padding: 8px 10px;
}

.epc-bi-states + .epc-bi-states {
  margin-top: 6px;
}

.epc-bi-states-layer {
  font-size: 11px;
  font-weight: 700;
  color: #4b5563;
  margin: 0 0 5px;
  text-transform: capitalize;
}

.epc-bi-state-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
}

.epc-bi-dot {
  flex: none;
  width: 11px;
  height: 11px;
  border-radius: 3px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
}

.epc-bi-state-name {
  flex: 1;
  min-width: 0;
  font-size: 11.5px;
  color: #4b5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.epc-bi-state-count {
  flex: none;
  font-size: 12px;
  font-weight: 700;
  color: #1f2937;
}

.epc-bi-empty {
  font-size: 12px;
  color: #b4bac4;
  padding: 2px 0;
}
</style>
